/**
 * Owner: Masa Bayu
 * File: AuthorAccessGate.tsx
 * Date:Time: 2026-10-23 08:15
 * Description: §467-C — gerbang peranan bagi semua laluan /author/*. Sesi penerbit
 *   qxp-hub (Bearer) ialah satu-satunya kunci: `GET /v1/publisher/dashboard`
 *   memulangkan 403 untuk sesi reader dan 401 jika token tiada/tamat, jadi status
 *   stor dashboard itu sendiri adalah keputusan gerbang — tidak ada tekaan
 *   peranan di pihak klien. Empat keadaan (muat / belum log masuk / bukan
 *   penerbit / ralat) dilukis sebagai skrin bertema QX, bukan teks mentah.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { getMarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import { useHubAuthState } from '../../publication/hub-auth';
import {
  refreshPublisherDashboard,
  resetPublisherDashboard,
  usePublisherDashboard,
} from './publisher-dashboard';
import { PRIVATE_AUTHOR_LABELS } from './private-author-labels';
import './private-author.css';

function Stroke({
  children,
  size = 22,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function GatePanel({
  title,
  body,
  icon,
  tone,
  actions,
}: {
  readonly title: string;
  readonly body: string;
  readonly icon: ReactNode;
  readonly tone: 'info' | 'warn';
  readonly actions: readonly ReactNode[];
}): ReactElement {
  return (
    <div className="qxp qxp__gate-root">
      <section
        className={`qxp__gate${tone === 'warn' ? ' qxp__gate--warn' : ''}`}
        role={tone === 'warn' ? 'alert' : 'status'}
        aria-label={title}
      >
        <span className="qxp__gate-ic" aria-hidden="true">
          {icon}
        </span>
        <h1 className="qxp__gate-title">{title}</h1>
        <p className="qxp__gate-body">{body}</p>
        <div className="qxp__gate-actions">{actions}</div>
      </section>
    </div>
  );
}

export interface AuthorAccessGateProps {
  readonly onNavigate: (path: string) => void;
  readonly children: ReactNode;
}

export function AuthorAccessGate({ onNavigate, children }: AuthorAccessGateProps): ReactElement {
  const profile = useMemo(() => getMarketplaceLocaleProfile(), []);
  const la = PRIVATE_AUTHOR_LABELS[profile.language];
  const auth = useHubAuthState();
  const dash = usePublisherDashboard();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (auth !== 'signed-in') {
      resetPublisherDashboard();
      return;
    }
    void refreshPublisherDashboard();
  }, [auth, reloadKey]);

  const retry = useCallback((): void => {
    setReloadKey((k) => k + 1);
  }, []);

  // §513 — `anonymous` ialah keadaan stor SEBELUM `refreshPublisherDashboard`
  // berjalan (kesan selepas paint pertama), bukan keadaan log keluar. Dahulu
  // keadaan ini melukis panel "Sign in required" kepada sesi yang sah, lalu
  // pengguna klik log masuk semula. Keputusan log keluar HANYA daripada `auth`.
  if (auth !== 'signed-in') {
    return (
      <GatePanel
        tone="info"
        title={la.gateSignedOutTitle}
        body={la.gateSignedOutBody}
        icon={
          <Stroke>
            <circle cx="12" cy="8" r="3.6" />
            <path d="M5 20a7 7 0 0 1 14 0" />
          </Stroke>
        }
        actions={[
          <button
            key="login"
            type="button"
            className="qxp__gate-btn qxp__gate-btn--primary"
            onClick={() => {
              onNavigate('/login');
            }}
          >
            {la.loginCta}
          </button>,
          <button
            key="author"
            type="button"
            className="qxp__gate-btn"
            onClick={() => {
              onNavigate('/become-an-author');
            }}
          >
            {la.publisherCta}
          </button>,
        ]}
      />
    );
  }

  if (dash.status === 'loading' || dash.status === 'anonymous') {
    return (
      <GatePanel
        tone="info"
        title={la.brand}
        body={la.gateLoading}
        icon={
          <Stroke>
            <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18" />
          </Stroke>
        }
        actions={[]}
      />
    );
  }

  if (dash.status === 'forbidden') {
    return (
      <GatePanel
        tone="warn"
        title={la.gateNotPublisherTitle}
        body={la.gateNotPublisherBody}
        icon={
          <Stroke>
            <path d="M4 20l1-4L16 5l3 3L8 19z" />
            <path d="M12 12l9 9M19 12l2-2" />
          </Stroke>
        }
        actions={[
          <button
            key="author"
            type="button"
            className="qxp__gate-btn qxp__gate-btn--primary"
            onClick={() => {
              onNavigate('/become-an-author');
            }}
          >
            {la.publisherCta}
          </button>,
          <button
            key="home"
            type="button"
            className="qxp__gate-btn"
            onClick={() => {
              onNavigate('/');
            }}
          >
            {la.backHome}
          </button>,
        ]}
      />
    );
  }

  if (dash.status === 'error') {
    return (
      <GatePanel
        tone="warn"
        title={la.gateErrorTitle}
        body={dash.error ?? la.noData}
        icon={
          <Stroke>
            <path d="M12 4l9 16H3z" />
            <path d="M12 10v4M12 17h.01" />
          </Stroke>
        }
        actions={[
          <button
            key="retry"
            type="button"
            className="qxp__gate-btn qxp__gate-btn--primary"
            onClick={retry}
          >
            {la.retry}
          </button>,
        ]}
      />
    );
  }

  return <>{children}</>;
}
